import { useEffect, useMemo, useReducer, useRef } from 'react'
import axios, { CancelTokenSource } from 'axios'
import { multipartUpload } from '@/services/s3'

export type IUploadStatus =
  | 'init'
  | 'pending'
  | 'uploading'
  | 'completed'
  | 'error'
export type IUploadPlatform = 'aliyun' | 'aws'

const INIT_STATE: IUploadState = {
  status: 'init',
}

type IAction = {
  type: IUploadStatus
  data?:
    | {
        uploadProgress: number
        target?: IUploadPlatform
      }
    | any
}

export type IUploadState = {
  status: IUploadStatus
  uploadProgress?: number
  target?: IUploadPlatform
  payload?: any
}

function reducer(st: IUploadState, action: IAction): IUploadState {
  switch (action.type) {
    case 'init':
      return {
        ...st,
        status: action.type,
        uploadProgress: 0,
      }
    case 'completed':
      return {
        ...st,
        status: action.type,
        uploadProgress: 0,
        payload: action.data.payload,
      }
    case 'pending':
    case 'error':
      return {
        ...st,
        status: action.type,
      }
    case 'uploading':
      if (!action.data) {
        throw new Error('Payload required')
      }
      return {
        ...st,
        status: action.type,
        uploadProgress: action.data.uploadProgress,
        target: action.data.target,
      }
    default:
      throw new Error('Unknown action')
  }
}

export type IUseUploader = ReturnType<typeof useUpload>

export default function useUpload() {
  const [currentState, dispatch] = useReducer(reducer, INIT_STATE)

  const cancelTokenRef = useRef<CancelTokenSource>()

  useEffect(() => {
    if (currentState.status === 'init' || currentState.status === 'completed') {
      cancelTokenRef.current = undefined
    }
  }, [currentState.status])

  const S3Upload = useMemo(() => {
    const multipart = async (signedUrl: string, file: Blob) => {
      try {
        const res = await multipartUpload(signedUrl, file, {
          onInit: tokenSrc => {
            cancelTokenRef.current = tokenSrc
          },
          onProgress: (progress: number) => {
            dispatch({
              type: 'uploading',
              data: {
                target: 'aws',
                uploadProgress: progress * 100,
              },
            })
          },
        })
        dispatch({
          type: 'completed',
          data: { payload: signedUrl },
        })
        return res
      } catch (e) {
        if (axios.isCancel(e)) {
          dispatch({ type: 'init' })
        } else {
          console.error(e)
          dispatch({ type: 'error' })
        }
      }
    }

    const cancelUpload = () => {
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel()
        dispatch({ type: 'init' })
      }
    }

    return {
      multipart,
      cancelUpload,
    }
  }, [])

  const reupload = () => {
    dispatch({
      type: 'init',
    })
  }

  const goPending = () => {
    dispatch({
      type: 'pending',
    })
  }

  return {
    S3Upload,
    currentState,
    reupload,
    goPending,
  }
}
